import * as React from 'react';
import { Component } from 'react';
import { parse } from 'query-string';
import BPromise from 'bluebird';
import moment from 'moment';
import { News } from 'Src/models/news';
import {
  ColumnDefinition,
  getFilters2,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter2,
  isFilterExist2,
  isSortExist,
  TABLE_SORT_DIRECTION
} from 'Src/helpers/table/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE, LIMIT } from 'Src/consts/table';
import { createNews, deleteNews, getAllNews, getNews, getNewsCount, updateNews } from 'Src/helpers/service/admin/news';
import { Loader } from 'Src/components/Loader/Loader';
import { SelectAllCheckbox } from 'Src/components/SelectAllCheckbox/SelectAllCheckbox';
import { NewsForm } from 'Src/components/NewsForm/NewsForm';
import { AppUser } from 'Src/views/App/App';
import './NewsList.css';
import { getNewsDate } from 'Src/helpers/accessor/accessor';
import { History, Location } from 'history';
import { SimpleModal } from 'Src/components/SimpleModal/SimpleModal';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { loadHiddenColumns, saveHiddenColumns } from 'Src/helpers/columnVisibility/columnVisibility';
import { ListEmptyState } from 'Src/components/ListEmptyState/ListEmptyState';
import { faNewspaper } from '@fortawesome/free-solid-svg-icons';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Date',
    field: 'date',
    isSort: true,
    type: FILTER_TYPE.DATE_INTERVAL,
    accessor: getNewsDate
  },
  {
    text: 'Title',
    field: 'title',
    isSort: true,
    type: FILTER_TYPE.TEXT,
    accessor: ['title']
  }
];

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

interface State {
  items: News[];
  currentPage: number;
  selectedItems: News[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;

  isEditNewsPopupOpened: boolean;
  isDeleteNewsModalOpen: boolean;
  hiddenColumns: string[];
  isColumnsOpen: boolean;
}

export class NewsList extends Component<Props, State> {
  private columnsRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',

      isEditNewsPopupOpened: false,
      isDeleteNewsModalOpen: false,
      hiddenColumns: loadHiddenColumns(props.user, 'communications.news'),
      isColumnsOpen: false
    };
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.onDocumentMouseDown);

    this.setState({
      isLoading: true
    });

    this.setItems();
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.onDocumentMouseDown);
  }

  onDocumentMouseDown = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;
    const shouldCloseColumns =
      this.state.isColumnsOpen &&
      this.columnsRef.current &&
      !this.columnsRef.current.contains(target);
    const shouldCloseFilters =
      this.state.isShowFilter &&
      !target.closest('.newsPage__filters') &&
      !target.closest('.newsPage__filterButton') &&
      !target.closest('.sit-dp__pop');

    if (shouldCloseColumns && shouldCloseFilters) {
      this.setState({ isColumnsOpen: false, isShowFilter: false });
    } else if (shouldCloseColumns) {
      this.setState({ isColumnsOpen: false });
    } else if (shouldCloseFilters) {
      this.setState({ isShowFilter: false });
    }
  };

  setHiddenColumns = (hiddenColumns: string[]): void => {
    saveHiddenColumns(this.props.user, 'communications.news', hiddenColumns);
    this.setState({ hiddenColumns });
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection: TABLE_SORT_DIRECTION = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters2(COLUMNS, search);

    const isDataFiltered = isFilterExist2(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter2(page, where, order);

    const getNewsPromise = getNews(user, serverQueryFilter);
    const getNewsCountPromise = getNewsCount(user, where);
    const promises = [getNewsCountPromise, getNewsPromise];

    return BPromise.all(promises).then(([countObj, news]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection,
        sortColumnsName: sortColumnsName,
        isShowFilter: false,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: news,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search: string[] = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedNews = items[index];

    const selectedNewsIndex = selectedItems.findIndex(news => selectedNews.id === news.id);
    let selectedNewsUpdated = [...selectedItems];

    if (selectedNewsIndex !== -1) {
      selectedNewsUpdated.splice(selectedNewsIndex, 1);
    } else {
      selectedNewsUpdated.push(selectedNews);
    }

    this.setState({
      selectedItems: selectedNewsUpdated
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const newsFiltered = items.filter(news => {
      return selectedItems.every(selectedNews => selectedNews.id !== news.id);
    });

    const selectedNews = [...selectedItems, ...newsFiltered];

    this.setState({
      selectedItems: selectedNews,
      isSelectAllChecked: true
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    getAllNews(user, where).then(news => {
      const newsFiltered = news.filter(news => {
        return selectedItems.every(selectedNews => selectedNews.id !== news.id);
      });

      const selectedNews = [...selectedItems, ...newsFiltered];

      this.setState({
        selectedItems: selectedNews,
        isSelectAllChecked: true,
        isLoading: false
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search: string[] = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters;

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...filters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue
            }
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...filters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue
            }
          };
          break;
        default:
          nextFilters = { ...filters };
          console.error('Can not set new filter');
      }
    } else {
      nextFilters = {
        ...filters,
        [filterField]: filterValue
      };
    }

    this.setState({
      filters: nextFilters
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search: string[] = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist2(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
      isShowFilter: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: search.join('&')
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false
    });

    this.props.history.push({
      pathname: this.state.basePath,
      search: ''
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter
    });
  };

  closeNewsFormModal = (): void => {
    this.setState({
      isEditNewsPopupOpened: false
    });
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      total: index
    });
  };

  onEditNewsClick = event => {
    event.preventDefault();

    this.setState({ isEditNewsPopupOpened: true });
  };

  onFormSubmit = data => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    switch (selectedItems.length) {
      case 0: {
        createNews(user, data).then(news => {
          this.closeNewsFormModal();
          this.setState({
            isLoading: true,
            selectedItems: [news]
          });
          this.setItems();
        });
        break;
      }

      case 1: {
        updateNews(user, selectedItems[0].id, data).then(news => {
          this.closeNewsFormModal();
          this.setState({
            isLoading: true,
            selectedItems: [news]
          });
          this.setItems();
        });
        break;
      }

      default:
        return;
    }
  };

  renderNewsForm() {
    const { isEditNewsPopupOpened, selectedItems } = this.state;

    switch (selectedItems.length) {
      case 0:
        return (
          <SimpleModal
            customClass="newsFormModal"
            isOpen={isEditNewsPopupOpened}
            title={'Create news'}
            showFooter={false}
            isBodyBold={false}
            onCloseClick={this.closeNewsFormModal}
          >
            <NewsForm user={this.props.user} onCloseClick={this.closeNewsFormModal} onFormSubmit={this.onFormSubmit} />
          </SimpleModal>
        );
      case 1:
        return (
          <SimpleModal
            customClass="newsFormModal"
            isOpen={isEditNewsPopupOpened}
            title={'Edit news'}
            showFooter={false}
            isBodyBold={false}
            onCloseClick={this.closeNewsFormModal}
          >
            <NewsForm
              user={this.props.user}
              onCloseClick={this.closeNewsFormModal}
              onFormSubmit={this.onFormSubmit}
              news={selectedItems[0]}
            />
          </SimpleModal>
        );
      default:
        return null;
    }
  }

  onCreateNewsClick = () => {
    this.setState({
      selectedItems: [],
      isEditNewsPopupOpened: true
    });
  };

  deleteNewsModalOpen = (): void => {
    this.setState({
      isDeleteNewsModalOpen: true
    });
  };

  deleteNewsModalClose = (): void => {
    this.setState({
      isDeleteNewsModalOpen: false
    });
  };

  onRemoveNewsClick = () => {
    const { selectedItems } = this.state;

    this.setState({ isLoading: true });
    const promises = selectedItems.map(news => {
      return deleteNews(this.props.user, news.id);
    });
    BPromise.all(promises).then(() => {
      this.setState({
        selectedItems: [],
        isDeleteNewsModalOpen: false
      });
      this.setItems();
    });
  };

  renderDeleteNewsModal() {
    const { selectedItems, isDeleteNewsModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isDeleteNewsModalOpen}
        title="Delete news?"
        showFooter={false}
        isBodyBold={false}
        customClass="newsDeleteModal"
        onCloseClick={this.deleteNewsModalClose}
      >
        <div className="newsDelete">
          <div className="newsDelete__icon">!</div>
          <p>
            {selectedItems.length === 1
              ? `“${selectedItems[0].title}” will be permanently deleted.`
              : `${selectedItems.length} selected news items will be permanently deleted.`}
          </p>
          <div className="newsDelete__actions">
            <button type="button" className="newsBtn newsBtn--ghost" onClick={this.deleteNewsModalClose}>
              Cancel
            </button>
            <button type="button" className="newsBtn newsBtn--danger" onClick={this.onRemoveNewsClick}>
              Delete
            </button>
          </div>
        </div>
      </SimpleModal>
    );
  }

  render() {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      selectedItems,
      filters,
      isLoading,
      isDataFiltered,
      currentPage,
      total,
      isEditNewsPopupOpened,
      hiddenColumns,
      isColumnsOpen
    } = this.state;

    if (isLoading) {
      return <Loader />;
    }
    const pageCount = Math.max(1, Math.ceil(total / LIMIT));
    const pageWindowStart = Math.max(1, Math.min(currentPage - 2, pageCount - 4));
    const pageWindowEnd = Math.min(pageCount, pageWindowStart + 4);
    const pageNumbers = Array.from(
      { length: pageWindowEnd - pageWindowStart + 1 },
      (_, index) => pageWindowStart + index
    );
    const selectedIds = new Set(selectedItems.map(item => item.id));
    const titleFilter = filters.title || '';
    const dateFilter = filters.date || {};
    const activeFilterCount = dateFilter.from || dateFilter.to ? 1 : 0;
    const hiddenColumnSet = new Set(hiddenColumns);
    const sortIndicator = (field: string) => (sortColumnsName === field ? (sortDirection === 'ASC' ? '↑' : '↓') : '↕');

    return (
      <div className={`newsPage ${isEditNewsPopupOpened ? 'modal-open' : ''}`}>
        {this.renderDeleteNewsModal()}
        {this.renderNewsForm()}
        <header className="newsPage__header">
          <h1 className="newsPage__title">
            News <span>{total}</span>
          </h1>
          <button className="newsBtn newsBtn--primary" onClick={this.onCreateNewsClick}>
            + Create news
          </button>
        </header>
        <section className="newsPage__tableCard">
          <div className="newsPage__toolbar">
            <label className="newsPage__search">
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-4-4" />
              </svg>
              <input
                value={titleFilter}
                placeholder="Search news"
                onChange={event => this.onTableFilterChange(event, 'title')}
                onKeyDown={event => event.key === 'Enter' && this.onApplyFilterClick()}
              />
              {titleFilter && (
                <button type="button" onClick={this.onClearFilterClick} aria-label="Clear search">
                  ×
                </button>
              )}
            </label>
            <button
              type="button"
              className={`newsPage__filterButton ${isShowFilter ? 'is-active' : ''}`}
              onClick={this.onTableFilterClick}
              aria-label="Filters"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M3 5h18M6 12h12M10 19h4" />
              </svg>
              Filters{activeFilterCount > 0 ? <b>{activeFilterCount}</b> : null}
            </button>
            <div className="newsPage__columns" ref={this.columnsRef}>
              <button
                type="button"
                className="newsBtn newsBtn--secondary newsPage__columnsButton"
                onClick={() => this.setState({ isColumnsOpen: !isColumnsOpen })}
              >
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path d="M4 5h16v14H4zM10 5v14M16 5v14" />
                </svg>
                Columns <span>{3 - hiddenColumns.length}</span>
              </button>
              {isColumnsOpen && (
                <div className="newsPage__columnsMenu">
                  <header>
                    <strong>Show / hide columns</strong>
                    <button type="button" onClick={() => this.setHiddenColumns([])}>
                      Reset
                    </button>
                  </header>
                  {[
                    { key: 'news', label: 'News' },
                    { key: 'date', label: 'Publish date' },
                    { key: 'status', label: 'Status' }
                  ].map(column => (
                    <label key={column.key}>
                      <input
                        type="checkbox"
                        checked={!hiddenColumnSet.has(column.key)}
                        onChange={() =>
                          this.setHiddenColumns(
                            hiddenColumnSet.has(column.key)
                              ? hiddenColumns.filter(key => key !== column.key)
                              : [...hiddenColumns, column.key]
                          )
                        }
                      />
                      {column.label}
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="newsPage__toolbarSpacer" />
          </div>
          {isShowFilter && (
            <section
              className="newsPage__filters"
              onKeyDown={event => {
                if (event.key === 'Enter' && (event.target as HTMLElement).tagName !== 'BUTTON') {
                  event.preventDefault();
                  this.onApplyFilterClick();
                }
              }}
            >
              <header>
                <strong>Filters</strong>
                <button type="button" onClick={() => this.setState({ isShowFilter: false })} aria-label="Close filters">
                  ×
                </button>
              </header>
              <div className="newsPage__filterBody">
                <div className="newsPage__filterDateField">
                  <span>Publish date</span>
                  <SitDatePicker
                    className="newsPage__dateRange"
                    mode="range"
                    value={{
                      start: dateFilter.from ? moment(dateFilter.from, 'DD-MM-YYYY').toDate() : null,
                      end: dateFilter.to ? moment(dateFilter.to, 'DD-MM-YYYY').toDate() : null
                    }}
                    onChange={(range: any) =>
                      this.onTableFilterChange(
                        {
                          target: {
                            value: {
                              from: range && range.start ? moment(range.start).format('DD-MM-YYYY') : '',
                              to: range && range.end ? moment(range.end).format('DD-MM-YYYY') : ''
                            }
                          }
                        },
                        'date'
                      )
                    }
                    format="DD-MM-YYYY"
                    numberOfMonths={2}
                    placeholder="Select date range"
                  />
                </div>
              </div>
              <footer className="newsPage__filterActions">
                <button type="button" className="newsBtn newsBtn--text is-clear" onClick={this.onClearFilterClick}>
                  Clear all
                </button>
                <div>
                  <button
                    type="button"
                    className="newsBtn newsBtn--text"
                    onClick={() => this.setState({ isShowFilter: false }, this.setItems)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="newsBtn newsBtn--primary is-apply"
                    onClick={this.onApplyFilterClick}
                  >
                    Apply
                  </button>
                </div>
              </footer>
            </section>
          )}
          {selectedItems.length > 0 && (
            <div className="newsPage__selection">
              <span>{selectedItems.length} selected</span>
              <div>
                {selectedItems.length === 1 && (
                  <button className="newsBtn newsBtn--ghost" onClick={this.onEditNewsClick}>
                    Edit
                  </button>
                )}
                <button className="newsBtn newsBtn--dangerGhost" onClick={this.deleteNewsModalOpen}>
                  Delete
                </button>
                <button className="newsPage__clear" onClick={this.onUnselectAllClick}>
                  Clear selection
                </button>
              </div>
            </div>
          )}
          <div className="newsPage__tableScroll">
            <table className="newsPage__table">
              <thead>
                <tr>
                  <th className="newsPage__check">
                    <SelectAllCheckbox
                      compact
                      isChecked={items.length > 0 && items.every(item => selectedIds.has(item.id))}
                      totalCount={total}
                      currentPage={currentPage}
                      pageItemsCount={items.length}
                      selectedItems={selectedItems}
                      onSelectAllOnPageClick={this.onSelectAllOnPageClick}
                      onSelectAllClick={this.onSelectAllClick}
                      onUnselectAllClick={this.onUnselectAllClick}
                    />
                  </th>
                  {!hiddenColumnSet.has('news') && (
                    <th>
                      <button onClick={() => this.onTableSortClick('title')}>News {sortIndicator('title')}</button>
                    </th>
                  )}
                  {!hiddenColumnSet.has('date') && (
                    <th>
                      <button onClick={() => this.onTableSortClick('date')}>
                        Publish date {sortIndicator('date')}
                      </button>
                    </th>
                  )}
                  {!hiddenColumnSet.has('status') && <th>Status</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((news, index) => {
                  const isScheduled = moment(news.date).isAfter(moment());
                  return (
                    <tr
                      key={news.id}
                      className={selectedIds.has(news.id) ? 'is-selected' : ''}
                      onClick={() => this.onItemClick(index)}
                    >
                      <td className="newsPage__check" onClick={event => event.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={selectedIds.has(news.id)}
                          onChange={() => this.onItemClick(index)}
                        />
                      </td>
                      {!hiddenColumnSet.has('news') && (
                        <td>
                          <div className="newsPage__newsCell">
                            {news.picUrl ? (
                              <img src={news.picUrl} alt="" />
                            ) : (
                              <span className="newsPage__placeholder">N</span>
                            )}
                            <div>
                              <strong>{news.title}</strong>
                              <small>
                                {String(news.body || '')
                                  .replace(/<[^>]+>/g, ' ')
                                  .replace(/\s+/g, ' ')
                                  .trim()
                                  .slice(0, 90) || 'No content'}
                              </small>
                            </div>
                          </div>
                        </td>
                      )}
                      {!hiddenColumnSet.has('date') && <td>{moment(news.date).format('D MMM YYYY, HH:mm')}</td>}
                      {!hiddenColumnSet.has('status') && (
                        <td>
                          <span className={`newsPage__status ${isScheduled ? 'is-scheduled' : 'is-published'}`}>
                            <i />
                            {isScheduled ? 'Scheduled' : 'Published'}
                          </span>
                        </td>
                      )}
                    </tr>
                  );
                })}
                {items.length === 0 && (
                  <tr>
                    <td colSpan={4 - hiddenColumns.length} className="newsPage__empty">
                      <ListEmptyState
                        icon={faNewspaper}
                        title={isDataFiltered ? 'No news matches your search' : 'No news yet'}
                        description={
                          isDataFiltered
                            ? 'Try another search term or change the publish date filter to see other news.'
                            : 'Published and scheduled school news will appear here.'
                        }
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        {total > 0 && (
          <nav className="newsPage__pagination">
            <span>
              Page {currentPage} of {pageCount}
            </span>
            <button disabled={currentPage <= 1} onClick={() => this.setCurrentPageParams(currentPage - 1)}>
              Previous
            </button>
            {pageWindowStart > 1 && (
              <>
                <button onClick={() => this.setCurrentPageParams(1)}>1</button>
                {pageWindowStart > 2 && <i>…</i>}
              </>
            )}
            {pageNumbers.map(page => (
              <button
                key={page}
                className={page === currentPage ? 'is-active' : ''}
                onClick={() => this.setCurrentPageParams(page)}
              >
                {page}
              </button>
            ))}
            {pageWindowEnd < pageCount && (
              <>
                {pageWindowEnd < pageCount - 1 && <i>…</i>}
                <button onClick={() => this.setCurrentPageParams(pageCount)}>{pageCount}</button>
              </>
            )}
            <button disabled={currentPage >= pageCount} onClick={() => this.setCurrentPageParams(currentPage + 1)}>
              Next
            </button>
          </nav>
        )}
      </div>
    );
  }
}
